import { createWorker } from 'tesseract.js'
import {
  extractLikelyCardTerms,
  hasStrongTitleConsensus,
  hasTrustedTitleTerm,
  mergeTerms,
  rankConsensusTerms,
} from './ocrTerms'
import type { CardGame, ScanResult } from './types'

let currentProgress: (label: string) => void = () => undefined
let workerPromise: ReturnType<typeof createWorker> | null = null
type CropMode = 'adaptive' | 'bright' | 'color' | 'contrast' | 'threshold' | 'invert'

const fullTextWhitelist = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 ,'-.—–"
const titleTextWhitelist = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz ,'-.—–"
const ignoredMtgSetCodes = new Set([
  'ART',
  'EN',
  'LLC',
  'TM',
  'WIZARDS',
])

export async function recognizeCardCanvas(
  canvas: HTMLCanvasElement,
  onProgress: (label: string) => void,
  game: CardGame = 'mtg',
): Promise<ScanResult> {
  currentProgress = onProgress
  const worker = await getWorker()

  try {
    await worker.setParameters({
      preserve_interword_spaces: '1',
      tessedit_pageseg_mode: '7' as Tesseract.PSM,
      tessedit_char_whitelist: fullTextWhitelist,
    })

    const titleTexts: string[] = []
    const titleTermGroups: string[][] = []

    for (const titleCrop of getTitleCrops(canvas, game)) {
      try {
        await worker.setParameters({
          preserve_interword_spaces: '1',
          tessedit_pageseg_mode: titleCrop.psm,
          tessedit_char_whitelist: titleTextWhitelist,
        })

        const titleResult = await worker.recognize(titleCrop.canvas)
        titleTexts.push(titleResult.data.text)
        titleTermGroups.push(
          extractLikelyCardTerms(titleResult.data.text, { game, titleOnly: true }),
        )

        if (
          titleTermGroups.length >= 3 &&
          hasStrongTitleConsensus(titleTermGroups, game)
        ) {
          break
        }
      } finally {
        releaseCanvas(titleCrop.canvas)
      }
    }

    const titleTerms = rankConsensusTerms(titleTermGroups)
    if (hasTrustedTitleTerm(titleTerms, game)) {
      return {
        rawText: titleTexts.join('\n').trim(),
        terms: titleTerms,
        setCodes: [],
        collectorNumbers: [],
      }
    }

    await worker.setParameters({
      preserve_interword_spaces: '1',
      tessedit_pageseg_mode: '6' as Tesseract.PSM,
      tessedit_char_whitelist: fullTextWhitelist,
    })

    const fullResult = await worker.recognize(canvas)
    const rawText = [...titleTexts, fullResult.data.text].join('\n').trim()
    const fullTerms = rankConsensusTerms([
      extractLikelyCardTerms(rawText, { game }),
    ])

    return {
      rawText,
      terms: mergeTerms(titleTerms, fullTerms),
      setCodes: [],
      collectorNumbers: [],
    }
  } finally {
    currentProgress = () => undefined
  }
}

export async function recognizeMtgPrintingDetails(
  canvas: HTMLCanvasElement,
  onProgress: (label: string) => void,
): Promise<Pick<ScanResult, 'collectorNumbers' | 'rawText' | 'setCodes'>> {
  currentProgress = onProgress
  const worker = await getWorker()

  try {
    return await recognizeMtgMetadata(worker, canvas)
  } finally {
    currentProgress = () => undefined
  }
}

function getWorker() {
  workerPromise ??= createWorker('eng', undefined, {
    logger: (message) => {
      if (message.status) {
        const progress = Math.round((message.progress ?? 0) * 100)
        currentProgress(`${message.status} ${progress}%`)
      }
    },
  }).catch((error) => {
    workerPromise = null
    throw error
  })

  return workerPromise
}

function* getTitleCrops(
  canvas: HTMLCanvasElement,
  game: CardGame,
): Generator<{ canvas: HTMLCanvasElement; psm: Tesseract.PSM }> {
  const pokemonRects = [
    // Full Pokemon title bars first: basic Pokemon, trainers, and energy cards
    // put the actual name at different top offsets.
    { x: 0.045, y: 0.04, width: 0.78, height: 0.07, scale: 9, mode: 'contrast', psm: '7' },
    { x: 0.045, y: 0.04, width: 0.78, height: 0.07, scale: 9, mode: 'threshold', psm: '7' },
    { x: 0.045, y: 0.07, width: 0.7, height: 0.07, scale: 9, mode: 'contrast', psm: '7' },
    { x: 0.045, y: 0.07, width: 0.7, height: 0.07, scale: 9, mode: 'threshold', psm: '7' },
    { x: 0.045, y: 0.095, width: 0.7, height: 0.065, scale: 9, mode: 'contrast', psm: '7' },
    { x: 0.045, y: 0.095, width: 0.7, height: 0.065, scale: 9, mode: 'threshold', psm: '7' },
    // Pokemon name band. These avoid the BASIC/STAGE label and HP area.
    { x: 0.16, y: 0.018, width: 0.5, height: 0.075, scale: 8, mode: 'contrast', psm: '7' },
    { x: 0.16, y: 0.018, width: 0.5, height: 0.075, scale: 8, mode: 'threshold', psm: '7' },
    { x: 0.19, y: 0.018, width: 0.47, height: 0.075, scale: 9, mode: 'contrast', psm: '7' },
    { x: 0.19, y: 0.018, width: 0.47, height: 0.075, scale: 9, mode: 'threshold', psm: '7' },
    { x: 0.22, y: 0.018, width: 0.45, height: 0.075, scale: 9, mode: 'bright', psm: '7' },
    { x: 0.26, y: 0.018, width: 0.42, height: 0.075, scale: 9, mode: 'contrast', psm: '7' },
    { x: 0.26, y: 0.018, width: 0.42, height: 0.075, scale: 9, mode: 'threshold', psm: '7' },
    { x: 0.3, y: 0.018, width: 0.38, height: 0.075, scale: 10, mode: 'contrast', psm: '7' },
    { x: 0.3, y: 0.018, width: 0.38, height: 0.075, scale: 10, mode: 'threshold', psm: '7' },
    { x: 0.115, y: 0.018, width: 0.58, height: 0.085, scale: 7, mode: 'bright', psm: '7' },
    { x: 0.115, y: 0.018, width: 0.58, height: 0.085, scale: 7, mode: 'threshold', psm: '7' },
    // Trainer/item cards put the card name under the TRAINER label.
    { x: 0.035, y: 0.062, width: 0.78, height: 0.08, scale: 7, mode: 'contrast', psm: '7' },
    { x: 0.035, y: 0.062, width: 0.78, height: 0.08, scale: 7, mode: 'threshold', psm: '7' },
    // Full Pokemon title strip fallback.
    { x: 0.025, y: 0.012, width: 0.9, height: 0.13, scale: 5, mode: 'contrast', psm: '7' },
  ] as const

  const mtgRects = [
    // Newer and borderless MTG frames place the title closer to the top edge.
    { x: 0.025, y: 0.018, width: 0.72, height: 0.062, scale: 8, mode: 'contrast', psm: '7' },
    { x: 0.025, y: 0.018, width: 0.72, height: 0.062, scale: 8, mode: 'adaptive', psm: '7' },
    { x: 0.025, y: 0.018, width: 0.72, height: 0.062, scale: 8, mode: 'color', psm: '7' },
    { x: 0.025, y: 0.018, width: 0.72, height: 0.062, scale: 8, mode: 'invert', psm: '7' },
    // MTG title bar on older black-bordered cards.
    { x: 0.025, y: 0.052, width: 0.645, height: 0.055, scale: 7, mode: 'contrast', psm: '8' },
    { x: 0.025, y: 0.052, width: 0.645, height: 0.055, scale: 7, mode: 'adaptive', psm: '8' },
    { x: 0.025, y: 0.052, width: 0.645, height: 0.055, scale: 7, mode: 'invert', psm: '8' },
    { x: 0.025, y: 0.047, width: 0.85, height: 0.068, scale: 8, mode: 'contrast', psm: '7' },
    { x: 0.025, y: 0.047, width: 0.85, height: 0.068, scale: 8, mode: 'adaptive', psm: '7' },
    { x: 0.025, y: 0.047, width: 0.85, height: 0.068, scale: 8, mode: 'invert', psm: '7' },
    { x: 0.025, y: 0.044, width: 0.887, height: 0.074, scale: 7, mode: 'color', psm: '7' },
    { x: 0.025, y: 0.047, width: 0.795, height: 0.07, scale: 8, mode: 'contrast', psm: '7' },
    { x: 0.025, y: 0.047, width: 0.795, height: 0.07, scale: 8, mode: 'threshold', psm: '7' },
    { x: 0.025, y: 0.044, width: 0.72, height: 0.07, scale: 8, mode: 'contrast', psm: '7' },
    { x: 0.025, y: 0.044, width: 0.72, height: 0.07, scale: 8, mode: 'adaptive', psm: '7' },
    { x: 0.025, y: 0.048, width: 0.713, height: 0.062, scale: 9, mode: 'bright', psm: '7' },
    { x: 0.025, y: 0.048, width: 0.713, height: 0.062, scale: 9, mode: 'threshold', psm: '7' },
    { x: 0.025, y: 0.045, width: 0.75, height: 0.075, scale: 6, mode: 'contrast', psm: '7' },
    { x: 0.025, y: 0.045, width: 0.75, height: 0.075, scale: 6, mode: 'threshold', psm: '7' },
    // Full top name bar: MTG, Pokemon basics, and many trainers.
    { x: 0.025, y: 0.015, width: 0.92, height: 0.14, scale: 4, mode: 'contrast', psm: '7' },
  ] as const
  const rects = game === 'pokemon' ? pokemonRects : mtgRects

  for (const rect of rects) {
    yield {
      canvas: cropCanvas(
        canvas,
        {
          x: Math.round(canvas.width * rect.x),
          y: Math.round(canvas.height * rect.y),
          width: Math.round(canvas.width * rect.width),
          height: Math.round(canvas.height * rect.height),
        },
        rect.scale,
        rect.mode,
      ),
      psm: rect.psm as Tesseract.PSM,
    }
  }
}

async function recognizeMtgMetadata(
  worker: Awaited<ReturnType<typeof getWorker>>,
  canvas: HTMLCanvasElement,
): Promise<{
  collectorNumbers: string[]
  rawText: string
  setCodes: string[]
}> {
  const metadataCrop = cropCanvas(
    canvas,
    {
      x: Math.round(canvas.width * 0.025),
      y: Math.round(canvas.height * 0.845),
      width: Math.round(canvas.width * 0.78),
      height: Math.round(canvas.height * 0.145),
    },
    5,
    'contrast',
  )

  await worker.setParameters({
    preserve_interword_spaces: '1',
    tessedit_pageseg_mode: '6' as Tesseract.PSM,
    tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789/ ',
  })

  let rawText = ''
  try {
    const result = await worker.recognize(metadataCrop)
    rawText = result.data.text.trim()
  } finally {
    releaseCanvas(metadataCrop)
  }

  return {
    rawText,
    ...extractMtgMetadata(rawText),
  }
}

function extractMtgMetadata(rawText: string): {
  collectorNumbers: string[]
  setCodes: string[]
} {
  const collectorNumbers = Array.from(
    rawText.matchAll(/\b(\d{1,4}[a-z]?)\s*\/\s*\d{1,4}\b/gi),
    (match) => match[1].toUpperCase(),
  )
  const setCodes = Array.from(
    rawText.toUpperCase().matchAll(/\b[A-Z][A-Z0-9]{1,4}\b/g),
    (match) => match[0],
  ).filter(
    (code) =>
      !ignoredMtgSetCodes.has(code) &&
      (code.length === 3 || /\d/.test(code)),
  )

  return {
    collectorNumbers: Array.from(new Set(collectorNumbers)).slice(0, 4),
    setCodes: Array.from(new Set(setCodes)).slice(0, 6),
  }
}

function cropCanvas(
  source: HTMLCanvasElement,
  rect: { x: number; y: number; width: number; height: number },
  scale = 1,
  mode: CropMode = 'contrast',
): HTMLCanvasElement {
  const crop = document.createElement('canvas')
  const context = crop.getContext('2d')

  const requestedWidth = Math.max(1, Math.round(rect.width * scale))
  const requestedHeight = Math.max(1, Math.round(rect.height * scale))
  const maximumLongEdge = 2501
  const boundedScale = Math.min(
    1,
    maximumLongEdge / Math.max(requestedWidth, requestedHeight),
  )

  crop.width = Math.max(1, Math.round(requestedWidth * boundedScale))
  crop.height = Math.max(1, Math.round(requestedHeight * boundedScale))

  if (context) {
    context.imageSmoothingEnabled = false
    context.filter =
      mode === 'threshold'
        ? 'grayscale(1) contrast(1.8)'
        : mode === 'color'
          ? 'contrast(1.15) brightness(1.05)'
        : mode === 'bright'
          ? 'grayscale(1) contrast(2.1) brightness(1.24)'
          : 'grayscale(1) contrast(1.45) brightness(1.08)'
    context.drawImage(
      source,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      0,
      0,
      crop.width,
      crop.height,
    )

    if (mode === 'threshold' || mode === 'invert') {
      thresholdCanvas(context, crop.width, crop.height)
    }

    if (mode === 'adaptive') {
      adaptiveThresholdCanvas(context, crop.width, crop.height)
    }

    if (mode === 'color') {
      maximizeColorCanvas(context, crop.width, crop.height)
      adaptiveThresholdCanvas(context, crop.width, crop.height)
    }

    if (mode === 'invert') {
      invertCanvas(context, crop.width, crop.height)
    }
  }

  return crop
}

function releaseCanvas(canvas: HTMLCanvasElement) {
  canvas.width = 1
  canvas.height = 1
}

function invertCanvas(context: CanvasRenderingContext2D, width: number, height: number) {
  const imageData = context.getImageData(0, 0, width, height)
  const data = imageData.data

  for (let index = 0; index < data.length; index += 4) {
    data[index] = 255 - data[index]
    data[index + 1] = 255 - data[index + 1]
    data[index + 2] = 255 - data[index + 2]
  }

  context.putImageData(imageData, 0, 0)
}

function maximizeColorCanvas(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
) {
  const imageData = context.getImageData(0, 0, width, height)
  const data = imageData.data

  for (let index = 0; index < data.length; index += 4) {
    const value = Math.max(data[index], data[index + 1], data[index + 2])
    data[index] = value
    data[index + 1] = value
    data[index + 2] = value
  }

  context.putImageData(imageData, 0, 0)
}

function thresholdCanvas(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
) {
  const imageData = context.getImageData(0, 0, width, height)
  const data = imageData.data
  const histogram = Array(256).fill(0) as number[]

  for (let index = 0; index < data.length; index += 4) {
    histogram[data[index]] += 1
  }

  const pixelCount = width * height
  const totalValue = histogram.reduce(
    (total, count, value) => total + count * value,
    0,
  )
  let backgroundCount = 0
  let backgroundValue = 0
  let bestVariance = -1
  let threshold = 165

  for (let value = 0; value < histogram.length; value += 1) {
    backgroundCount += histogram[value]

    if (!backgroundCount || backgroundCount === pixelCount) {
      continue
    }

    backgroundValue += value * histogram[value]
    const foregroundCount = pixelCount - backgroundCount
    const backgroundMean = backgroundValue / backgroundCount
    const foregroundMean = (totalValue - backgroundValue) / foregroundCount
    const variance =
      backgroundCount *
      foregroundCount *
      (backgroundMean - foregroundMean) ** 2

    if (variance > bestVariance) {
      bestVariance = variance
      threshold = value
    }
  }

  for (let index = 0; index < data.length; index += 4) {
    const value = data[index] <= threshold ? 0 : 255
    data[index] = value
    data[index + 1] = value
    data[index + 2] = value
  }

  context.putImageData(imageData, 0, 0)
}

function adaptiveThresholdCanvas(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
) {
  const imageData = context.getImageData(0, 0, width, height)
  const data = imageData.data
  const integralWidth = width + 1
  const integral = new Float64Array(integralWidth * (height + 1))
  const radius = Math.max(9, Math.round(height * 0.08))

  for (let y = 1; y <= height; y += 1) {
    let rowTotal = 0

    for (let x = 1; x <= width; x += 1) {
      rowTotal += data[((y - 1) * width + x - 1) * 4]
      integral[y * integralWidth + x] =
        integral[(y - 1) * integralWidth + x] + rowTotal
    }
  }

  for (let y = 0; y < height; y += 1) {
    const top = Math.max(0, y - radius)
    const bottom = Math.min(height - 1, y + radius)

    for (let x = 0; x < width; x += 1) {
      const left = Math.max(0, x - radius)
      const right = Math.min(width - 1, x + radius)
      const area = (right - left + 1) * (bottom - top + 1)
      const sum =
        integral[(bottom + 1) * integralWidth + right + 1] -
        integral[top * integralWidth + right + 1] -
        integral[(bottom + 1) * integralWidth + left] +
        integral[top * integralWidth + left]
      const index = (y * width + x) * 4
      const value = data[index] < sum / area - 11 ? 0 : 255

      data[index] = value
      data[index + 1] = value
      data[index + 2] = value
    }
  }

  context.putImageData(imageData, 0, 0)
}
